/**
 * utils/importWipe.js
 * Pure helpers backing the import wizard's two "start from empty" step-1 options.
 *
 *  - isUntouchedSeed(data): true ONLY when the app is EXACTLY the fresh 7-item seed — the
 *    same 7 internalCodes AND the same stock (stokPusat/stokCabang) as buildSeedData()'s own
 *    canonical items. An 8th item, a deleted seed item, or a changed stock value all make
 *    this false — "remove the sample items" must only ever discard exactly what
 *    buildSeedData() itself created, never a user's real edits.
 *  - wipedData(current): a fresh, fully-empty data object (defaultData's own shape, cloned so
 *    callers never share references with the module-level default), preserving `current`'s
 *    _normVersion — a wipe resets data, it is not a migration rollback.
 *
 * Both are the single source of truth shared by ImportWizard's two checkboxes (and App.js's
 * commit handler), so the "is this the untouched seed" and "what does empty look like"
 * questions are answered in exactly one, tested place.
 */
import { defaultData } from "./storage";
import { buildSeedData } from "./seedData";

export const SEED_CODES = ["SG-00001", "SG-00002", "SG-00003", "SG-00004", "SG-00005", "SG-00006", "SG-00007"];

/** True only when data.items is exactly the 7 fresh seed items (same codes AND same stock). */
export function isUntouchedSeed(data) {
  const items = data?.items || [];
  if (items.length !== 7) return false;
  const seedItems = buildSeedData().items;
  const byCode = new Map(items.map((it) => [it.internalCode, it]));
  return seedItems.every((seedItem) => {
    const it = byCode.get(seedItem.internalCode);
    return !!it
      && Number(it.stokPusat) === Number(seedItem.stokPusat)
      && Number(it.stokCabang) === Number(seedItem.stokCabang);
  });
}

/** A fresh, fully-empty data object — defaultData's shape, preserving current's _normVersion.
 *  Deep-cloned via JSON round-trip (defaultData is plain, JSON-safe data) rather than
 *  `structuredClone` — same convention `seedData.js`'s own buildSeedData already uses, and
 *  unlike `structuredClone` it's available in every environment this app runs in, including
 *  Jest's jsdom test environment (which does not polyfill `structuredClone`). */
export function wipedData(current) {
  return {
    ...JSON.parse(JSON.stringify(defaultData)),
    _normVersion: current?._normVersion,
  };
}
